import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import SportFilter from '../components/shared/SportFilter';
import { toggleBetSlip } from '../utils/betSlip';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

const css = `.sp-list{padding:6px 14px 80px}.sp-card{background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:14px;margin-bottom:10px;overflow:hidden;cursor:pointer}.sp-card:active{border-color:rgba(0,212,170,.3)}.sp-card-head{display:flex;align-items:center;justify-content:space-between;padding:7px 12px;border-bottom:1px solid rgba(255,255,255,.04)}.sp-card-league{display:flex;align-items:center;gap:6px}.sp-card-league img{width:14px;height:14px;object-fit:contain}.sp-card-league-name{font-size:11px;color:#5a5972;font-weight:600}.sp-card-date{font-size:11px;color:#5a5972}.sp-card-body{display:flex;align-items:center;padding:10px 12px;gap:10px}.sp-teams{flex:1;display:flex;flex-direction:column;gap:6px;min-width:0}.sp-team{display:flex;align-items:center;gap:8px}.sp-team img{width:20px;height:20px;object-fit:contain;flex-shrink:0}.sp-team-name{font-size:13px;font-weight:600;color:#e4e7ec;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.sp-odds{display:flex;gap:5px;flex-shrink:0}.sp-odd-btn{display:flex;flex-direction:column;align-items:center;padding:7px 8px;min-width:42px;border-radius:8px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.03);cursor:pointer}.sp-odd-btn.selected{border-color:rgba(0,212,170,.6);background:rgba(0,212,170,.12)}.sp-odd-label{font-size:10px;color:#5a5972;font-weight:700;margin-bottom:2px}.sp-odd-value{font-size:12px;color:#e4e7ec;font-weight:700}.sp-odd-btn.selected .sp-odd-label,.sp-odd-btn.selected .sp-odd-value{color:#00d4aa}.sp-no-odds{font-size:11px;color:#3a3857}.sp-spinner{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;gap:14px}.sp-spinner-ring{width:36px;height:36px;border:3px solid rgba(255,255,255,.06);border-top-color:#00d4aa;border-radius:50%;animation:sp-spin 1s linear infinite}@keyframes sp-spin{to{transform:rotate(360deg)}}.sp-spinner p{color:#5a5972;font-size:13px;margin:0}.sp-empty{display:flex;align-items:center;justify-content:center;padding:60px 20px}.sp-empty p{color:#3a3857;font-size:13px}`;

function fmtDate(ts) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function parseOdds(match) {
  if (!match.odds?.match_winner?.length) return [];
  return match.odds.match_winner.map((o, i) => ({
    label: o.label === 'Home' ? '1' : o.label === 'Away' ? '2' : o.label,
    betType: `Match Winner: ${o.label}`, value: o.odd, index: i,
  }));
}

export default function MMA() {
  const { t } = useLang();
  const [matches, setMatches] = useState([]);
  const [visible, setVisible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vCount, setVCount] = useState(10);
  const [selBets, setSelBets] = useState([]);
  const lmRef = useRef(); const obsRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync(); window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  useEffect(() => { fetchMatches(); }, []);
  useEffect(() => { setVisible(matches.slice(0, vCount)); }, [matches, vCount]);

  const loadMore = useCallback(() => { if (vCount < matches.length) setVCount(p => p + 10); }, [vCount, matches.length]);
  useEffect(() => {
    if (obsRef.current) obsRef.current.disconnect();
    obsRef.current = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { threshold: 0.1 });
    if (lmRef.current) obsRef.current.observe(lmRef.current);
    return () => { if (obsRef.current) obsRef.current.disconnect(); };
  }, [loadMore]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const k = 'mma_matches'; const c = localStorage.getItem(k);
      if (c) { const { data, ts } = JSON.parse(c); if (Date.now() - ts < 36e5) { setMatches(data); return; } }
      const res = await fetch(`${API}/api-bet/mma/matchs.php`);
      const data = await res.json();
      if (data.success) {
        const list = data.games || data.matchs || data.matches || [];
        localStorage.setItem(k, JSON.stringify({ data: list, ts: Date.now() }));
        setMatches(list);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleOdd = (e, match, betType, oddLabel, oddValue, oddIndex) => {
    e.stopPropagation();
    const id = match.id || match.fixture?.id;
    const ts = match.timestamp || match.fixture?.timestamp;
    const [market] = betType.split(':');
    const fixtureDate = ts ? new Date(ts * 1000).toISOString() : '';
    toggleBetSlip({ id: `mma-${id}-${oddIndex}`, matchId: id, match: `${match.teams?.home?.name || match.fighter1 || '?'} vs ${match.teams?.away?.name || match.fighter2 || '?'}`, homeTeam: match.teams?.home?.name || match.fighter1, awayTeam: match.teams?.away?.name || match.fighter2, date: ts ? new Date(ts * 1000).toLocaleString('fr-FR') : '', fixtureDate, selection: betType, betType: market.trim(), betLabel: oddLabel, handicap: null, odds: parseFloat(oddValue), league: match.event || match.league?.name || 'MMA', isLive: false, sport: 'mma' });
  };

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <BackHeader title="MMA" />
      <SportFilter />
      {loading ? (
        <div className="sp-spinner"><div className="sp-spinner-ring" /><p>{t('sports.loading')}</p></div>
      ) : matches.length === 0 ? (
        <div className="sp-empty"><p>{t('sports.noUpcoming')}</p></div>
      ) : (
        <div className="sp-list">
          {visible.map(match => {
            const id = match.id || match.fixture?.id;
            const ts = match.timestamp || match.fixture?.timestamp;
            const odds = parseOdds(match);
            const home = match.teams?.home?.name || match.fighter1 || '?';
            const away = match.teams?.away?.name || match.fighter2 || '?';
            return (
              <div key={id} className="sp-card" onClick={() => navigate(`/match/mma/${id}`)}>
                <div className="sp-card-head">
                  <div className="sp-card-league"><span className="sp-card-league-name">{match.event || match.league?.name || 'MMA'}</span></div>
                  <span className="sp-card-date">{fmtDate(ts)}</span>
                </div>
                <div className="sp-card-body">
                  <div className="sp-teams">
                    <div className="sp-team">{match.teams?.home?.logo && <img src={match.teams.home.logo} alt="" />}<span className="sp-team-name">{home}</span></div>
                    <div className="sp-team">{match.teams?.away?.logo && <img src={match.teams.away.logo} alt="" />}<span className="sp-team-name">{away}</span></div>
                  </div>
                  {odds.length > 0 ? (
                    <div className="sp-odds" onClick={e => e.stopPropagation()}>
                      {odds.map(o => <button key={o.index} className={'sp-odd-btn' + (selBets.includes(`mma-${id}-${o.index}`) ? ' selected' : '')} onClick={e => handleOdd(e, match, o.betType, o.label, o.value, o.index)}><span className="sp-odd-label">{o.label}</span><span className="sp-odd-value">{o.value}</span></button>)}
                    </div>
                  ) : <span className="sp-no-odds">N/A</span>}
                </div>
              </div>
            );
          })}
          {vCount < matches.length && <div ref={lmRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="sp-spinner-ring" style={{ width: 24, height: 24, borderWidth: 2 }} /></div>}
        </div>
      )}
    </PhoneShell>
  );
}
